import { createGlobalState } from '@vueuse/core';
import * as sdk from '@cirqll/sdk';
import { DefaultRole } from '@cirqll/sdk/enum';
import { routes as generatedRoutes, type RouteNamedMap } from 'vue-router/auto-routes';

type Authenticated = ReturnType<typeof sdk.useAuthenticated>;
type RouteGuard = sdk.Permission[] | ((auth: Authenticated) => boolean);

/**
 * Single source of truth for which permissions unlock which routes.
 * Used both to guard direct navigation (see modules/auth.ts) and to hide
 * nav links a user can't access, so the two never drift apart.
 */
export const route_guards: Partial<Record<keyof RouteNamedMap, RouteGuard>> = {
    '/overview/customers': ['customer.read'],
    '/overview/contacts': ['contact.read'],
    '/overview/notes': ['note.read', 'overview_note.read'],
    '/overview/appointments': ['appointment.read'],
    '/overview/tasks': ['task.read'],
    '/overview/chances': ['chance.read'],
    '/overview/assignments': ['assignment.read'],
    '/overview/contracts': ['contract.read'],
    '/dashboard/financial': ['chance.read', 'assignment.read', 'contract.read'],
    '/settings/subscription': ({ user }) => user.value.role.name === DefaultRole.COMPANY_OWNER as string,
    '/settings/marketplace': ({ permitted }) => permitted('external_client.read') || permitted('client.read'),
    '/settings/log': ['log.read'],
};

// Detached global scope so this can be read from outside components (the router guard in
// modules/auth.ts) while still reusing the exact same permission logic every page relies on.
const useSharedAuthenticated = createGlobalState(() => sdk.useAuthenticated());

export function isRouteAllowed(name: keyof RouteNamedMap): boolean {
    const guard = route_guards[name];

    if (!guard) {
        return true;
    }

    const auth = useSharedAuthenticated();

    return Array.isArray(guard)
        ? auth.permitted(...guard as [sdk.Permission, ...sdk.Permission[]])
        : guard(auth);
}

/**
 * The path of the first allowed child route (by `meta.order`), or the path itself when it has none.
 */
export function firstChildPath(path: string): string {
    const children = generatedRoutes.find(route => route.path === path)?.children ?? [];
    const [first] = children
        .filter(child => typeof child.name === 'string' && isRouteAllowed(child.name as keyof RouteNamedMap))
        .sort((a, b) => Number(a.meta?.order ?? 0) - Number(b.meta?.order ?? 0));

    return typeof first?.name === 'string' ? first.name : path;
}
